import React, { useEffect, useState } from "react";
import { RefreshCw, Loader2, AlertCircle, Package, Factory } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";

/**
 * Phase 101 (12/07/2026) — Widget « Besoin production agrégé Nayax ».
 *
 * Agrège les manques (`missing_qty`) sur tous les derniers snapshots machines,
 * groupé par SKU AM. Affiche une table avec Manque total · Machines · Prod suggérée.
 *
 * Idée : servi sur la page /production comme guide de prod hebdo.
 */
export function ProductionNeedsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/production/nayax-needs");
      setData(r.data);
    } catch (e) {
      setData({ needs: [], total_snapshots: 0, error: e?.response?.data?.detail || "Erreur" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const needs = data?.needs || [];

  return (
    <section
      className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden"
      data-testid="production-needs-widget"
    >
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E5E5E0] bg-[#FAFAF7]">
        <div className="flex items-center gap-2">
          <Factory className="w-4 h-4 text-[#1F4E3D]" />
          <h3 className="font-medium text-sm text-[#1A1C1A]">Besoin production · agrégé Nayax</h3>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          size="sm"
          variant="ghost"
          className="h-8 text-xs"
          data-testid="production-needs-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
      </header>

      {loading && !data && (
        <div className="px-4 py-8 text-center text-sm text-[#8C8C88]">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement...
        </div>
      )}

      {data && data.total_snapshots === 0 && (
        <div className="px-4 py-8 text-center text-sm text-[#8C8C88]" data-testid="needs-no-snapshots">
          <AlertCircle className="w-4 h-4 inline mr-2" />
          Aucun snapshot machine encore synchronisé. Synchronisez au moins un distributeur pour alimenter cette vue.
        </div>
      )}

      {data && data.total_snapshots > 0 && needs.length === 0 && (
        <div className="px-4 py-8 text-center text-sm text-[#1F4E3D] bg-[#E8F2EC]" data-testid="needs-empty">
          ✅ Toutes les machines sont pleines. Rien à produire pour l&apos;instant.
        </div>
      )}

      {needs.length > 0 && (
        <>
          <div className="px-4 py-2 text-[11px] text-[#8C8C88] border-b border-[#E5E5E0] bg-white">
            Basé sur {data.total_snapshots} machines synchronisées ·{" "}
            <strong>{needs.reduce((s, n) => s + n.total_missing, 0)}</strong> barquettes manquantes au total
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF7] text-[10px] uppercase tracking-wider text-[#575A57]">
                <tr>
                  <th className="px-3 py-2 text-left">Produit</th>
                  <th className="px-3 py-2 text-right w-24">Manque total</th>
                  <th className="px-3 py-2 text-right w-24 hidden sm:table-cell">Machines</th>
                  <th className="px-3 py-2 text-right w-32">Prod suggérée</th>
                </tr>
              </thead>
              <tbody>
                {needs.map((n) => (
                  <tr
                    key={n.product_id}
                    className="border-t border-[#F0F0EC] hover:bg-[#FDFDFB]"
                    data-testid={`needs-row-${n.product_id}`}
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Package className="w-3.5 h-3.5 text-[#8C8C88] flex-shrink-0" />
                        <span className="text-[#1A1C1A]">{n.product_name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[#8C6B2F] font-medium">
                      {n.total_missing}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-[#575A57] hidden sm:table-cell">
                      <span className="cursor-help" title={n.machines.join(", ")}>
                        {n.machines_count}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="inline-block bg-[#1F4E3D] text-white px-2 py-0.5 rounded font-mono text-sm">
                        {n.suggested_production}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 text-[10px] text-[#8C8C88] bg-[#FDFDFB]">
            💡 <em>Prod suggérée</em> = manque total arrondi au multiple de 10 supérieur. À ajuster selon capacité + saison.
          </div>
        </>
      )}
    </section>
  );
}

export default ProductionNeedsWidget;
